import React, { useEffect, useState } from 'react';
import '../styled/pages/dashboard.scss';
import { useDispatch, useSelector } from 'react-redux';
import { addStudyMarking } from '../rtk-apis/reports/addStudyMarkingApi';
import { addStudyTopicApi } from '../rtk-apis/reports/addStudyTopicApi';
import { getStudyTopicsApi } from '../rtk-apis/reports/getStudyTopicsApi';
import Loader from './Loader';
import Success from './Success';
import Failed from './Failed';
import { clearTopicState } from '../store/slices/reports/addStudyTopic';
import { AppDispatch, RootState } from '../store/store';

type Props = {
  onClose?: () => void;
  isSelectTopicModalOpen: boolean;
  studyInstanceUID: string;
};

const SelectTopicModal = ({
  onClose,
  isSelectTopicModalOpen,
  studyInstanceUID,
  setIsAddResearchChecked,
}: Props) => {
  const dispatch = useDispatch<AppDispatch>();
  const [selectedTopic, setSelectedTopic] = useState('');
  const [loading, setLoading] = useState(false);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [openFailureModal, setOpenFailureModal] = useState(false);
  const [message, setMessage] = useState('');
  const [topicsList, setTopicsList] = useState([]);
  const [addTopicMode, setAddTopicMode] = useState(false);
  const [newTopic, setNewTopic] = useState('');

  const getStudyTopicsResponse = useSelector((state: RootState) => state.getStudyTopicsResponse);
  const addStudyTopicResponse = useSelector((state: RootState) => state.addStudyTopicResponse);

  const handleSaveTopic = () => {
    if (addTopicMode && newTopic) {
      const payload = {
        topics: [newTopic],
      };
      dispatch(addStudyTopicApi(payload));
    } else {
      if (!selectedTopic) {
        setOpenFailureModal(true);
        setMessage('Please select a topic before saving.');
        return;
      }

      setLoading(true);
      const payload = {
        studyResearchList: [{ topic: selectedTopic, studyInstanceUID: studyInstanceUID }],
        alertList: [],
      };
      dispatch(addStudyMarking(payload))
        .then(() => {
          setOpenSuccessModal(true);
          setMessage('Teaching file saved successfully!');
          setIsAddResearchChecked(true);
          onClose();
        })
        .catch(() => {
          setOpenFailureModal(true);
          setMessage('Failed to save teaching file.');
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value === 'AddTopic') {
      setAddTopicMode(true);
      setSelectedTopic('');
    } else {
      setSelectedTopic(value);
      setAddTopicMode(false);
    }
  };

  useEffect(() => {
    if (isSelectTopicModalOpen) {
      dispatch(getStudyTopicsApi());
    }
  }, [isSelectTopicModalOpen]);

  useEffect(() => {
    if (isSelectTopicModalOpen && getStudyTopicsResponse?.status === 'SUCCESS') {
      setTopicsList(getStudyTopicsResponse?.result?.data?.[0]?.topicList);
    }
  }, [getStudyTopicsResponse, isSelectTopicModalOpen]);

  useEffect(() => {
    if (isSelectTopicModalOpen && addStudyTopicResponse?.status === 'SUCCESS') {
      setOpenSuccessModal(true);
      setMessage('Topic added successfully!');
      setAddTopicMode(false);
      setNewTopic('');
      dispatch(clearTopicState());
      dispatch(getStudyTopicsApi());
    } else if (isSelectTopicModalOpen && addStudyTopicResponse?.status === 'FAILED') {
      setOpenFailureModal(true);
      setMessage('Failed to add topic.');
    }
  }, [addStudyTopicResponse, isSelectTopicModalOpen]);

  return (
    <>
      <div className={`modal modal--selectTopic ${isSelectTopicModalOpen ? 'modal--open' : ''}`}>
        <div
          className="modal__dialog"
          style={{ width: '50%' }}
        >
          <div
            className="modal__content modal__content--lg"
            style={{ width: '60%' }}
          >
            <div className="modal__header">
              <h1 className="modal__header--title">Select a Topic for teaching file:</h1>
              {/* <p className="para">Choose a topic from the list below.</p> */}
            </div>
            <div className="modal__body modal-body-left">
              {loading ? (
                <Loader isLoading />
              ) : (
                <div className="formRow mb_0">
                  <div className="formCol">
                    {addTopicMode ? (
                      <div className="form__group">
                        <label
                          htmlFor="newTopic"
                          className="form__label"
                        >
                          Add New Topic
                        </label>
                        <input
                          type="text"
                          id="newTopic"
                          className="form__input form__input--sm text-white"
                          value={newTopic}
                          onChange={e => setNewTopic(e.target.value)}
                        />
                      </div>
                    ) : (
                      <div className="form__group">
                        <label
                          htmlFor="selectedTopic"
                          className="form__label"
                        >
                          Select Topic
                        </label>
                        <select
                          id="selectedTopic"
                          className="form__select form__select--sm text-black"
                          value={selectedTopic}
                          onChange={handleSelectChange}
                        >
                          <option value="">-Select-</option>
                          <option
                            value="AddTopic"
                            style={{ fontWeight: 'bold', backgroundColor: '#f0f8ff' }}
                          >
                            + Add New Topic
                          </option>
                          {topicsList?.map((topic, index) => (
                            <option
                              key={index}
                              value={topic}
                            >
                              {topic}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="modal__footer">
              <div className="btn__group">
                <button
                  className="btn btn__white btn__outlined"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  className="btn btn__primary"
                  onClick={handleSaveTopic}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
        <div
          className="modal__backdrop"
          onClick={onClose}
        ></div>
      </div>
      {openSuccessModal && (
        <Success
          title={message}
          isSuccessModalOpen={openSuccessModal}
          handleCloseModal={() => setOpenSuccessModal(false)}
        />
      )}
      {openFailureModal && (
        <Failed
          title={message}
          isFailedModalOpen={openFailureModal}
          handleCloseModal={() => setOpenFailureModal(false)}
        />
      )}
    </>
  );
};

export default SelectTopicModal;
